import axios from "axios";
import { api } from "./api";
import { orderEndpoints, invoiceEndPoints } from "../constants/endpoints";
import { getApiOrganizationId } from "../utils/orgIdUtils";
import {
  CustomerOrder,
  SellerOrder,
  Pagination,
  OrderPayloadProps,
  OrderFilterStatus,
} from "../types/Order";
import {
  transformToCustomerOrders,
  transformToSellerOrders,
} from "../utils/orderMapping";
import { UpdateOrderPayloadType } from "../dto/orderSummaryTypes";

export type FetchOrdersProps = {
  emailId: string;
  page: number;
  limit: number;
  search?: string;
  sortOrder?: string;
  filter?: OrderFilterStatus;
  paymentStatus?: string;
  customer?: string;
};

export const fetchOrders = async ({
  emailId,
  page,
  limit,
  search,
  sortOrder,
  filter,
  paymentStatus,
  customer,
}: FetchOrdersProps): Promise<{
  orders: CustomerOrder[];
  pagination: Pagination;
}> => {
  try {
    const params: any = {
      //emailId,
      page,
      limit,
      search,
      sortOrder,
      orderStatus: filter ? orderFilterStatusMap.get(filter) : undefined,
      paymentStatus: paymentStatus || undefined,
      customer: customer || undefined,
    };

    const response = await api.get(orderEndpoints.getOrders(emailId), {
      params: {
        ...params,
      },
      headers: {
        orgId: getApiOrganizationId(),
        organizationId: getApiOrganizationId(),
      },
    });

    const transformedOrders = transformToCustomerOrders(
      response.data.data.orders
    );

    return {
      orders: transformedOrders,
      pagination: response.data.data.pagination,
    };
  } catch (error) {
    console.error("Error fetching orders by customer ID:", error);
    throw error;
  }
};

const orderFilterStatusMap = new Map<OrderFilterStatus, string>([
  ["New", "new"],
  ["Order Approved", "order_approved"],
  ["Processing", "processing"],
  ["Ready for Dispatch", "ready_for_dispatch"],
  ["Dispatched", "dispatched"],
  ["Delivered", "delivered"],
  ["Cancelled", "cancelled"],
]);

export const fetchOrdersSeller = async ({
  emailId,
  page,
  limit,
  search,
  sortOrder,
  filter,
  paymentStatus,
  customer,
}: FetchOrdersProps): Promise<{
  orders: SellerOrder[];
  pagination: Pagination;
}> => {
  try {
    const paymentStatusMap: Record<string, string> = {
      Complete: "SUCCESS",
      Pending: "PENDING",
    };
    const params: any = {
      page,
      limit,
      search,
      sortOrder,
      orderStatus: filter ? orderFilterStatusMap.get(filter) : undefined,
      paymentStatus: paymentStatus
        ? paymentStatusMap[paymentStatus]
        : undefined,
      customer: customer || undefined,
    };

    const response = await api.get(orderEndpoints.getOrders(emailId), {
      params: {
        ...params,
      },
      headers: {
        orgId: getApiOrganizationId(),
      },
    });

    const transformedOrders = transformToSellerOrders(
      response.data.data.orders
    );

    return {
      orders: transformedOrders,
      pagination: response.data.data.pagination,
    };
  } catch (error) {
    console.error("Error fetching orders by customer ID:", error);
    throw error;
  }
};

export const createOrder = async (
  payload: OrderPayloadProps
): Promise<void> => {
  console.log("payload++", payload);
  try {
    const response = await api.post(orderEndpoints.createOrder, payload, {
      headers: {
        orgId: getApiOrganizationId(),
      },
    });
    console.log("Order created successfully:", response.data);
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Axios error:", error.response?.data || error.message);
    } else {
      console.error("Unexpected error:", error);
    }
    throw error;
  }
};

export const getOrderById = async (
  orderId: string | undefined
): Promise<any> => {
  try {
    const response = await api.get(`orders/${orderId}`);
    console.log("Order fetched successfully:", response.data);
    return response;
  } catch (error) {
    console.error("Error fetching order details:", error);
    return undefined;
  }
};

export const triggerOrderPayment = async (
  orderId: string | undefined
): Promise<any> => {
  try {
    const response = await api.put(
      `trigger-payment/${orderId}`,
      {
        triggerPayment: true,
      },
      {
        headers: {
          orgId: getApiOrganizationId(),
        },
      }
    );
    console.log("Order payment triggered successfully:", response.data);
    return response;
  } catch (error) {
    console.error("Error triggering order payment:", error);
    return undefined;
  }
};

export const updateOrderAPI = async (
  orderId: string,
  payload: {
    orderDetails: UpdateOrderPayloadType[];
  }
) => {
  try {
    const response = await api.post(
      orderEndpoints.updateOrder(orderId),
      payload,
      {
        headers: {
          orgId: getApiOrganizationId(),
        },
      }
    );
    console.log("Order updated successfully:", response.data);
    return response;
  } catch (error) {
    console.error("Unexpected error:", error);
    throw error;
  }
};

export const fetchOrderDetails = async (orderId: string) => {
  const endpointurl = `${orderEndpoints.getOrder}/${orderId}`;
  try {
    const response = await api.get(endpointurl, {
      headers: {
        orgId: getApiOrganizationId(),
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch order details:", error);
  }
};

export const toggleIncludeService = async (
  orderId: string,
  includeService: boolean
): Promise<any> => {
  try {
    const response = await api.put(`/toggle-include-service/${orderId}`, {
      includeService,
      headers: {
        orgId: getApiOrganizationId(),
      },
    });
    console.log("Order service toggled successfully:", response.data);
    return response.data.data;
  } catch (error) {
    console.error("Error toggling includeService:", error);
    throw error;
  }
};

export const downloadInvoiceAPI = async (orderId: string): Promise<any> => {
  try {
    const response = await api.get(
      `${invoiceEndPoints.downloadInvoice}/${orderId}`,
      {
        headers: {
          orgId: getApiOrganizationId(),
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error downloading invoice:", error);
    throw error;
  }
};

export const editOrderAPI = async (
  orderId: string,
  payload: {
    newStatus?: string;
    eta?: string;
  }
): Promise<any> => {
  try {
    const response = await api.put(
      `${orderEndpoints.editOrder}/${orderId}`,
      payload,
      {
        headers: {
          orgId: getApiOrganizationId(),
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error editing order:", error);
    throw error;
  }
};

export const fetchOrderIds = async (customerId: string): Promise<string[]> => {
  try {
    const response = await api.get(orderEndpoints.getOrderIds(customerId), {
      headers: {
        orgId: getApiOrganizationId(),
      },
    });
    return response.data.data ?? [];
  } catch (error) {
    console.error("Error fetching order IDs:", error);
    return [];
  }
};
